import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, BookOpen, Clock, ExternalLink, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ReactMarkdown from "react-markdown";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { SEO } from "@/components/SEO";

interface ResourcePreview {
  id: string;
  slug: string;
  title: string;
  category: string;
  summary: string | null;
  tags: string[];
  reading_time: number | null;
}

interface ResourceFull extends ResourcePreview {
  content_md: string | null;
  sources: any;
}

const ITEMS_PER_PAGE = 12;

const Resources = () => {
  const [filteredResources, setFilteredResources] = useState<ResourcePreview[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedResource, setSelectedResource] = useState<ResourceFull | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // React Query: Cache automatique des ressources avec staleTime de 5 minutes
  const { data: resources = [], isLoading: loading } = useQuery({
    queryKey: ['rdr_resources'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rdr_resources")
        .select("id, slug, title, category, summary, tags, reading_time")
        .order("created_at", { ascending: false });

      if (error) {
        toast({
          title: "Erreur",
          description: "Impossible de charger les ressources",
          variant: "destructive",
        });
        throw error;
      }
      return data as ResourcePreview[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes de cache
    gcTime: 10 * 60 * 1000, // 10 minutes avant garbage collection
  });

  const totalCount = resources.length;

  useEffect(() => {
    filterResources();
  }, [searchQuery, selectedCategory, resources]);

  // Prefetch du contenu au survol (BOOST!)
  const prefetchContent = async (resourceId: string) => {
    await queryClient.prefetchQuery({
      queryKey: ['resource_content', resourceId],
      queryFn: async () => {
        const { data } = await supabase
          .from("rdr_resources")
          .select("content_md, sources")
          .eq("id", resourceId)
          .maybeSingle();
        return data;
      },
      staleTime: 10 * 60 * 1000, // 10 minutes
    });
  };

  const loadResourceContent = async (resource: ResourcePreview) => {
    setLoadingContent(true);
    setSelectedResource({
      ...resource,
      content_md: null,
      sources: null,
    });
    
    try {
      // Utiliser le cache de React Query si disponible
      const cachedData = queryClient.getQueryData(['resource_content', resource.id]) as { content_md: string | null; sources: any } | undefined;
      
      // Si le cache existe ET contient du contenu, on l'utilise. Sinon on refetch.
      if (cachedData && cachedData.content_md) {
        setSelectedResource({
          ...resource,
          ...cachedData,
        });
        setLoadingContent(false);
        return;
      }

      const { data, error } = await supabase
        .from("rdr_resources")
        .select("content_md, sources")
        .eq("id", resource.id)
        .maybeSingle();

      if (error) {
        console.error("Erreur chargement contenu:", error);
      }
      
      // Stocker dans le cache
      queryClient.setQueryData(['resource_content', resource.id], data);
      
      setSelectedResource({
        ...resource,
        content_md: data?.content_md || null,
        sources: data?.sources || null,
      });
    } catch (error) {
      console.error("Erreur:", error);
      setSelectedResource({
        ...resource,
        content_md: null,
        sources: null,
      });
    } finally {
      setLoadingContent(false);
    }
  };

  const filterResources = () => {
    let filtered = resources;

    if (selectedCategory !== "all") {
      filtered = filtered.filter((r) => r.category === selectedCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(
        (r) =>
          r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.tags?.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    setFilteredResources(filtered);
  };

  const categories = ["all", ...Array.from(new Set(resources.map((r) => r.category)))];

  // Pagination
  const totalPages = Math.ceil(filteredResources.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedResources = filteredResources.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory]);

  if (loading) {
    return (
      <div className="min-h-dvh bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-24 text-center">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-48 mx-auto" />
            <div className="h-4 bg-muted rounded w-96 mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Ressources sur la réduction des risques"
        description="Guides et informations sur la réduction des risques liés aux addictions. Ressources validées par des professionnels de santé. Articles, conseils pratiques et outils."
        canonical="/resources"
      />
      <div className="min-h-dvh bg-background">
      <Navbar />
      
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
              <BookOpen className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Ressources RDR</span>
            </div>
            
            <h1 className="text-5xl font-bold">
              Réduction des{" "}
              <span className="text-primary">
                Risques
              </span>
            </h1>
            
            <p className="text-lg text-muted-foreground">
              Informations fiables, validées et sans jugement sur les pratiques de réduction des risques
            </p>

            {totalCount > 0 && (
              <p className="text-sm text-muted-foreground">
                {filteredResources.length} ressource{filteredResources.length > 1 ? "s" : ""} sur {totalCount}
              </p>
            )}

            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Rechercher une ressource, un tag..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 text-base"
              />
            </div>

            <div className="flex flex-wrap justify-center gap-2">
              {categories.map((cat) => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                  className={selectedCategory === cat ? "bg-background" : ""}
                >
                  {cat === "all" ? "Toutes" : cat}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          {filteredResources.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <p className="text-xl text-muted-foreground">Aucune ressource trouvée</p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedResources.map((resource) => (
                  <Card
                    key={resource.id}
                    className="group p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer border-border"
                    onClick={() => loadResourceContent(resource)}
                    onMouseEnter={() => prefetchContent(resource.id)}
                  >
                    <div className="space-y-4">
                      <div className="flex items-start justify-between gap-2">
                        <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                          {resource.category}
                        </Badge>
                        {resource.reading_time && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            <span>{resource.reading_time} min</span>
                          </div>
                        )}
                      </div>

                      <h3 className="text-xl font-semibold line-clamp-2 group-hover:text-primary transition-colors">
                        {resource.title}
                      </h3>

                      {resource.summary && (
                        <p className="text-sm text-muted-foreground line-clamp-3">
                          {resource.summary}
                        </p>
                      )}

                      {resource.tags && resource.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {resource.tags.slice(0, 3).map((tag, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {resource.tags.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{resource.tags.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}

                      <Button variant="ghost" size="sm" className="w-full mt-2">
                        Lire l'article
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>

              {totalPages > 1 && (
                <Pagination className="mt-12">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <PaginationItem key={page}>
                            <PaginationLink
                              onClick={() => setCurrentPage(page)}
                              isActive={currentPage === page}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      }
                      if (page === currentPage - 2 || page === currentPage + 2) {
                        return (
                          <PaginationItem key={page}>
                            <span className="px-4">...</span>
                          </PaginationItem>
                        );
                      }
                      return null;
                    })}

                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </>
          )}
        </div>
      </section>

      <Dialog open={!!selectedResource} onOpenChange={() => setSelectedResource(null)}>
        <DialogContent className="max-w-3xl">
          {loadingContent ? (
            <DialogBody className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </DialogBody>
          ) : selectedResource && (
            <>
              <DialogHeader>
                <div className="space-y-4">
                  <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 w-fit">
                    {selectedResource.category}
                  </Badge>
                  <DialogTitle className="text-3xl">{selectedResource.title}</DialogTitle>
                  
                  {selectedResource.reading_time && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>{selectedResource.reading_time} minutes de lecture</span>
                    </div>
                  )}
                </div>
              </DialogHeader>

              <DialogBody className="prose prose-sm max-w-none break-words dark:prose-invert">
                {selectedResource.content_md ? (
                  <ReactMarkdown>{selectedResource.content_md}</ReactMarkdown>
                ) : (
                  <div className="space-y-4">
                    {selectedResource.summary && (
                      <p className="text-muted-foreground">{selectedResource.summary}</p>
                    )}
                    <div className="bg-muted/50 border border-border rounded-lg p-4">
                      <p className="text-sm text-muted-foreground">
                        ℹ️ Le contenu complet de cette ressource est en cours d'ajout.
                      </p>
                    </div>
                  </div>
                )}
                {selectedResource.tags && selectedResource.tags.length > 0 && (
                  <div className="not-prose mt-6 border-t pt-4">
                  <h4 className="text-sm font-medium mb-2">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedResource.tags.map((tag, idx) => (
                      <Badge key={idx} variant="outline">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  </div>
                )}

                {selectedResource.sources && Array.isArray(selectedResource.sources) && selectedResource.sources.length > 0 && (
                  <div className="not-prose mt-6 border-t pt-4">
                  <h4 className="text-sm font-medium mb-2">Sources</h4>
                  <div className="space-y-2">
                    {selectedResource.sources.map((source: any, idx: number) => (
                      <a
                        key={idx}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex min-h-11 items-center gap-2 break-all text-sm text-primary hover:underline"
                      >
                        <ExternalLink className="w-4 h-4" />
                        {source.title}
                      </a>
                    ))}
                  </div>
                  </div>
                )}
              </DialogBody>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
    </>
  );
};

export default Resources;
